'use client';

import React from 'react';

/**
 * Button mode types for different visual styles
 */
export type ButtonMode = 'primary' | 'secondary' | 'danger';

/**
 * Button size types
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Button component properties
 */
export interface ButtonProps {
  children: React.ReactNode;
  mode?: ButtonMode;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  fullWidth?: boolean;
  hoverStyle?: 'fill' | 'glow';
}

/**
 * Neon-themed button component with multiple styles and states
 * @param {ButtonProps} props - Button properties
 * @returns {JSX.Element} Styled button with neon glow effects
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  mode = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  fullWidth = false,
  hoverStyle = 'fill',
}) => {
  const baseClasses = 'font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 relative';

  const modeClasses = {
    primary: 'border-2 border-cyan-400 text-cyan-400 focus:ring-cyan-400',
    secondary: 'border-2 border-fuchsia-400 text-fuchsia-400 focus:ring-fuchsia-400',
    danger: 'border-2 border-danger text-danger focus:ring-danger',
  } as const;

  const hoverFillClasses = {
    primary: 'hover:bg-cyan-400 hover:text-black hover:neon-glow-primary',
    secondary: 'hover:bg-fuchsia-400 hover:text-black hover:neon-glow-secondary',
    danger: 'hover-fill-danger hover:neon-glow-danger',
  }

  const sizeClasses = {
    sm: 'py-1 px-3 text-sm',
    md: 'py-2 px-4',
    lg: 'py-3 px-6 text-lg',
  } as const;

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed border-gray-600 text-gray-600' : '';
  const widthClasses = fullWidth ? 'w-full' : '';
  const hoverClass = hoverStyle === 'fill' && !disabled ? hoverFillClasses[mode] : '';

  const classes = `${baseClasses} ${modeClasses[mode]} ${sizeClasses[size]} ${disabledClasses} ${widthClasses} ${hoverClass} ${className}`.trim();

  return (
    <button
      x-component-name="Button"
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </button>
  );
};